import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

const OAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();

  
  useEffect(() => {
    const code = searchParams.get("code");
    const error = searchParams.get("error");
    const state = searchParams.get("state");
    const idToken = searchParams.get("id_token"); // Apple sends id_token directly

    if (error) {
      // Handle error case for all providers
      const provider = state === "apple_login" ? "apple" :
        state === "facebook_login" ? "facebook" : "google";
      if (window.opener) {
        window.opener.postMessage({
          [`${provider}Error`]: error,
          [`${provider}ErrorDescription`]: searchParams.get("error_description")
        }, window.location.origin);
        window.close();
      }
      return;
    }

    // Determine provider based on state or presence of id_token
    if (state === "apple_login" || idToken) {
      // Apple Sign-In callback - process the id_token directly from the redirect
      if (idToken) {
        processAppleToken(idToken);
      } else if (code) {
        // Fallback to API call if only code is available
        exchangeAppleCodeForTokens(code, "");
      }
    } else if (state === "facebook_login") {
      // Facebook OAuth callback
      if (code) {
        exchangeFacebookCodeForTokens(code);
      }
    } else if (code) {
      // Google OAuth callback
      exchangeGoogleCodeForTokens(code);
    }
  }, [searchParams]);

  // Process Apple token directly from redirect (no API call needed)
  const processAppleToken = (idToken: string) => {
    try {
      const clientId = localStorage.getItem('ClientID');
      console.log("🍎 Processing Apple token directly from redirect");
      
      // Since this is coming from our backend redirect, we can trust it
      // Call our backend to exchange the token for a JWT
      exchangeAppleTokenDirectly(idToken);
    } catch (error) {
      console.error("Error processing Apple token:", error);
      if (window.opener) {
        window.opener.postMessage({
          appleError: "authentication_failed",
          appleErrorDescription: error instanceof Error ? error.message : "Unknown error"
        }, window.location.origin);
        window.close();
      }
    }
  };

  const exchangeGoogleCodeForTokens = async (code: string) => {
    try {
      const clientId = localStorage.getItem('ClientID');
      const mode = localStorage.getItem('OAuthMode') || 'login';
      const dateOfBirth = localStorage.getItem('OAuthDateOfBirth') || '';
      
      // Build request body with mode and optional dateOfBirth
      const requestBody: any = {
        code,
        redirect_uri: "https://webchat.supportedbusiness.com/auth/callback",
        clientId,
        mode
      };
      
      // Only include dateOfBirth if in register mode
      if (mode === 'register' && dateOfBirth) {
        requestBody.dateOfBirth = dateOfBirth;
      }
      
      const response = await fetch("/api/consumer/auth/google/callback", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      // Check if response has content before parsing JSON
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        // Log response details for debugging
        const responseText = await response.text();
        console.error("Google API non-JSON response:", {
          status: response.status,
          statusText: response.statusText,
          contentType,
          responseText: responseText.substring(0, 200)
        });
        throw new Error(`Google API returned ${response.status}. Expected JSON response.`);
      }

      const data = await response.json();

      // Handle requiresDob response - new user needs to enter DOB
      if (response.ok && data.requiresDob) {
        console.log("📋 Google OAuth requires DOB collection:", data);
        if (window.opener) {
          window.opener.postMessage({
            requiresDob: true,
            provider: "google",
            email: data.email,
            name: data.name,
            oauthSessionToken: data.oauthSessionToken, // Use session token instead of code
            clientId: data.clientId
          }, window.location.origin);
          window.close();
        }
        return;
      }

      if (response.ok && data.token) {
        if (window.opener) {
          window.opener.postMessage({
            googleToken: data.token,
            consumerId: data.consumerId,
            email: data.email,
            credits: data.credits,
            clientId: data.clientId
          }, window.location.origin);
          window.close();
        }
      } else {
        throw new Error(data.message || "Google login failed");
      }
    } catch (error) {
      console.error("Error exchanging Google code for tokens:", error);
      if (window.opener) {
        window.opener.postMessage({
          googleError: "authentication_failed",
          googleErrorDescription: error instanceof Error ? error.message : "Unknown error"
        }, window.location.origin);
        window.close();
      }
    }
  };

  const exchangeFacebookCodeForTokens = async (code: string) => {
    try {
      const clientId = localStorage.getItem('ClientID');
      const mode = localStorage.getItem('OAuthMode') || 'login';
      const dateOfBirth = localStorage.getItem('OAuthDateOfBirth') || '';

      // Build request body with mode and optional dateOfBirth
      const requestBody: any = {
        Code: code,
        RedirectUri: "https://webchat.supportedbusiness.com/auth/callback",
        ClientId: clientId ? parseInt(clientId, 10) : null,
        Mode: mode
      };
      
      // Only include dateOfBirth if in register mode
      if (mode === 'register' && dateOfBirth) {
        requestBody.DateOfBirth = dateOfBirth;
      }

      const response = await fetch("/api/consumer/auth/facebook/callback", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      // Check if response has content before parsing JSON
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Invalid response format from server");
      }

      const data = await response.json();

      // Handle requiresDob response - new user needs to enter DOB
      if (response.ok && data.requiresDob) {
        console.log("📋 Facebook OAuth requires DOB collection:", data);
        if (window.opener) {
          window.opener.postMessage({
            requiresDob: true,
            provider: "facebook",
            email: data.email,
            name: data.name,
            oauthSessionToken: data.oauthSessionToken, // Use session token instead of code
            clientId: data.clientId
          }, window.location.origin);
          window.close();
        }
        return;
      }

      if (response.ok && data.token) {
        if (window.opener) {
          window.opener.postMessage({
            facebookToken: data.token,
            consumerId: data.consumerId,
            email: data.email,
            credits: data.credits,
            clientId: data.clientId
          }, window.location.origin);
          window.close();
        }
      } else {
        throw new Error(data.message || "Facebook login failed");
      }
    } catch (error) {
      console.error("Error exchanging Facebook code for tokens:", error);
      if (window.opener) {
        window.opener.postMessage({
          facebookError: "authentication_failed",
          facebookErrorDescription: error instanceof Error ? error.message : "Unknown error"
        }, window.location.origin);
        window.close();
      }
    }
  };

  const exchangeAppleCodeForTokens = async (code: string, idToken: string) => {
    try {
      const clientId = localStorage.getItem('ClientID');
      const mode = localStorage.getItem('OAuthMode') || 'login';
      const dateOfBirth = localStorage.getItem('OAuthDateOfBirth') || '';

      // Build request body with mode and optional dateOfBirth
      const requestBody: any = {
        code,
        id_token: idToken,
        redirect_uri: "https://webchat.supportedbusiness.com/auth/callback",
        clientId,
        mode
      };
      
      // Only include dateOfBirth if in register mode
      if (mode === 'register' && dateOfBirth) {
        requestBody.dateOfBirth = dateOfBirth;
      }

      const response = await fetch("/api/consumer/auth/apple/callback", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      // Check if response has content before parsing JSON
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Invalid response format from server");
      }

      const data = await response.json();

      // Handle requiresDob response - new user needs to enter DOB
      if (response.ok && data.requiresDob) {
        console.log("📋 Apple OAuth requires DOB collection:", data);
        if (window.opener) {
          window.opener.postMessage({
            requiresDob: true,
            provider: "apple",
            email: data.email,
            name: data.name,
            oauthSessionToken: data.oauthSessionToken, // Use session token instead of idToken
            clientId: data.clientId
          }, window.location.origin);
          window.close();
        }
        return;
      }

      if (response.ok && data.token) {
        if (window.opener) {
          window.opener.postMessage({
            appleToken: data.token,
            consumerId: data.consumerId,
            email: data.email,
            credits: data.credits,
            clientId: data.clientId
          }, window.location.origin);
          window.close();
        }
      } else {
        throw new Error(data.message || "Apple login failed");
      }
    } catch (error) {
      console.error("Error exchanging Apple code for tokens:", error);
      if (window.opener) {
        window.opener.postMessage({
          appleError: "authentication_failed",
          appleErrorDescription: error instanceof Error ? error.message : "Unknown error"
        }, window.location.origin);
        window.close();
      }
    }
  };

  const exchangeAppleTokenDirectly = async (idToken: string) => {
    try {
      const clientId = localStorage.getItem('ClientID');
      const mode = localStorage.getItem('OAuthMode') || 'login';
      const dateOfBirth = localStorage.getItem('OAuthDateOfBirth') || '';

      // Build request body with mode and optional dateOfBirth
      const requestBody: any = {
        token: idToken,
        clientId: clientId,
        mode
      };
      
      // Only include dateOfBirth if in register mode
      if (mode === 'register' && dateOfBirth) {
        requestBody.dateOfBirth = dateOfBirth;
      }

      const response = await fetch("/api/consumer/auth/apple", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      // Check if response has content before parsing JSON
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        // Log response details for debugging
        const responseText = await response.text();
        console.error("Apple API non-JSON response:", {
          status: response.status,
          statusText: response.statusText,
          contentType,
          responseText: responseText.substring(0, 200),
          url: "/api/consumer/auth/apple"
        });
        throw new Error(`Apple API returned ${response.status}. Expected JSON response.`);
      }

      const data = await response.json();

      // Handle requiresDob response - new user needs to enter DOB
      if (response.ok && data.requiresDob) {
        console.log("📋 Apple OAuth requires DOB collection:", data);
        if (window.opener) {
          window.opener.postMessage({
            requiresDob: true,
            provider: "apple",
            email: data.email,
            name: data.name,
            oauthSessionToken: data.oauthSessionToken, // Use session token instead of idToken
            clientId: data.clientId
          }, window.location.origin);
          window.close();
        }
        return;
      }

      if (response.ok && data.token) {
        if (window.opener) {
          window.opener.postMessage({
            appleToken: data.token,
            consumerId: data.consumerId,
            email: data.email,
            credits: data.credits,
            clientId: data.clientId
          }, window.location.origin);
          window.close();
        }
      } else {
        throw new Error(data.message || "Apple login failed");
      }
    } catch (error) {
      console.error("Error with Apple direct token:", error);
      if (window.opener) {
        window.opener.postMessage({
          appleError: "authentication_failed",
          appleErrorDescription: error instanceof Error ? error.message : "Unknown error"
        }, window.location.origin);
        window.close();
      }
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(135deg, var(--color-primary) 0%, #fff 100%)"
    }}>
      <div style={{
        background: "#fff",
        borderRadius: 16,
        boxShadow: "0 4px 24px rgba(171,44,41,0.10)",
        padding: "40px 32px",
        textAlign: "center"
      }}>
        <div className="loader" style={{ margin: "0 auto 16px" }}></div>
        <h3 style={{ color: "var(--color-primary)", margin: 0 }}>Processing Authentication...</h3>
        <p style={{ color: "#666", margin: "8px 0 0 0" }}>Please wait while we complete your authentication.</p>
      </div>
    </div>
  );
};

export default OAuthCallback;